import { Link } from "react-router-dom";
import { ArrowRight, Settings2, Sparkles, Smartphone, LayoutGrid, MapPin, FileText, Calendar, ShieldCheck, MessageSquare, CreditCard, DollarSign, Bell, Building2, Rotate3D, Boxes, Users, Camera, Lock, Package, Workflow, Clock } from "lucide-react";
import CTABand from "@/components/CTABand";
import AIChat from "@/components/AIChat";
import { useSeo } from "@/lib/seo";
import { useReveal } from "@/lib/reveal";

const PILLARS = [
  {
    key: "flex",
    title: "Flexibility",
    icon: Settings2,
    sub: "Model any rental, any rules — without a rebuild.",
    accent: "from-ra-ocean/10 to-ra-oceanLight/0",
    features: [
      { icon: Boxes, name: "Dimensions engine", body: "Duration, capacity, pricing rules, add-ons — composed per SKU." },
      { icon: LayoutGrid, name: "Per-unit rate cards", body: "Hourly, daily, weekly, seasonal — stacked or standalone." },
      { icon: Workflow, name: "Custom workflows", body: "Different intake for kayaks vs. UTVs. No forked products." },
      { icon: Clock, name: "Time-slot scheduling", body: "Tours, classes, multi-stop trips with capacity rules." },
      { icon: Package, name: "Bundles & packages", body: "Sell a board, boots, and helmet as one line item." },
      { icon: Users, name: "Group bookings", body: "Split payments, per-guest intake, corporate accounts." },
    ],
  },
  {
    key: "ai",
    title: "AI Booking",
    icon: Sparkles,
    sub: "Renters self-serve. Owners convert more.",
    accent: "from-ra-amber/15 to-ra-amber/0",
    features: [
      { icon: Sparkles, name: "Conversational booking", body: "Natural-language search against your live inventory." },
      { icon: MessageSquare, name: "Smart follow-ups", body: "Reminders, upsells, post-trip review nudges — automated." },
      { icon: Camera, name: "AI damage detection", body: "Photo intake at return flags scratches and missing gear." },
      { icon: FileText, name: "Auto-summarised intake", body: "Renter notes captured and summarised for the front desk." },
      { icon: Lock, name: "Risk scoring", body: "Detect high-risk bookings — deposit tiers adjust automatically." },
      { icon: Workflow, name: "AI ops copilot", body: "Ask 'what's my best Saturday slot to add?' and get the answer." },
    ],
  },
  {
    key: "mobile",
    title: "Mobile Ops",
    icon: Smartphone,
    sub: "Built for the dock, the yard, the trailhead.",
    accent: "from-emerald-400/15 to-emerald-300/0",
    features: [
      { icon: MapPin, name: "GPS inventory", body: "Live unit tracking with geofence alerts." },
      { icon: Camera, name: "Photo check-in/out", body: "Damage-stamped, timestamped, pinned to the rental." },
      { icon: MessageSquare, name: "SMS & email", body: "All renter comms from one thread, one team number." },
      { icon: CreditCard, name: "Integrated POS", body: "Tap-to-pay, add-ons at return, one ledger." },
      { icon: DollarSign, name: "On-the-fly pricing", body: "Surge, holiday, weather adjustments in seconds." },
      { icon: ShieldCheck, name: "Fleet protection", body: "Deposits, damage waivers, audit trail per unit." },
    ],
  },
  {
    key: "platform",
    title: "All-in-One Platform",
    icon: LayoutGrid,
    sub: "One product. One bill. One team that knows your fleet.",
    accent: "from-violet-400/15 to-violet-300/0",
    features: [
      { icon: ShieldCheck, name: "Digital waivers", body: "Signed at checkout. Dated. Searchable." },
      { icon: Calendar, name: "Seasonal calendars", body: "Holidays, weather blackouts, peak/off-peak rules." },
      { icon: Rotate3D, name: "Refunds & holds", body: "Partial refunds, deposits, credits, rollovers." },
      { icon: Bell, name: "Auto reminders", body: "Renter shows up with the right gear, every time." },
      { icon: Building2, name: "Multi-location", body: "Share or silo inventory across branches." },
      { icon: CreditCard, name: "Built-in payments", body: "ACH, card, deposits, net-30 contractors." },
    ],
  },
];

export default function Product() {
  useSeo({
    title: "Product · More than just a booking calendar",
    description:
      "Explore the full RentAnything.ai feature set — flexible dimensions engine, AI booking, mobile operations, and a complete platform under one roof.",
    path: "/product",
  });
  useReveal();

  return (
    <>
      {/* HERO */}
      <section className="ra-mesh">
        <div className="ra-container py-20 sm:py-28">
          <div className="max-w-3xl">
            <span className="ra-pill">
              <span className="h-1.5 w-1.5 rounded-full bg-ra-ocean" />
              The full feature catalog
            </span>
            <h1 className="mt-5 ra-h1">More than a booking calendar.</h1>
            <p className="mt-5 ra-lead">
              RentAnything is four products in one platform. Configure the parts you need today; the rest is already waiting when you're ready.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/contact" className="ra-btn-primary" data-testid="product-hero-demo-btn">
                Book a Demo <ArrowRight size={16} />
              </Link>
              <Link to="/pricing" className="ra-btn-ghost" data-testid="product-hero-pricing-btn">
                See pricing
              </Link>
            </div>
          </div>

          <div className="mt-14 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {PILLARS.map((p) => {
              const Icon = p.icon;
              return (
                <a
                  key={p.key}
                  href={`#pillar-${p.key}`}
                  className="group ra-card flex flex-col gap-3"
                  data-testid={`pillar-jump-${p.key}`}
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-ra-ocean/10 text-ra-ocean">
                    <Icon size={20} />
                  </div>
                  <h3 className="font-display text-lg font-semibold text-ra-ink">{p.title}</h3>
                  <p className="text-sm text-ra-ink/70">{p.sub}</p>
                  <span className="mt-auto text-xs font-semibold text-ra-ocean opacity-0 transition-opacity group-hover:opacity-100">
                    Jump to section →
                  </span>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* PILLARS */}
      {PILLARS.map((p, idx) => {
        const Icon = p.icon;
        return (
          <section
            key={p.key}
            id={`pillar-${p.key}`}
            className={`ra-section ${idx % 2 === 1 ? "bg-ra-paper border-y border-ra-line" : ""}`}
          >
            <div className="ra-container">
              <div className="grid gap-10 lg:grid-cols-[1fr_1.4fr] lg:items-start">
                <div className="reveal lg:sticky lg:top-24">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-ra-ocean/10 text-ra-ocean">
                    <Icon size={22} />
                  </div>
                  <h2 className="mt-5 ra-h2">{p.title}</h2>
                  <p className="mt-3 ra-lead max-w-md">{p.sub}</p>
                  {p.key === "ai" && (
                    <div className="mt-6 hidden lg:block">
                      <AIChat />
                    </div>
                  )}
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {p.features.map((f) => {
                    const FI = f.icon;
                    return (
                      <div key={f.name} className="ra-card reveal" data-testid={`feature-${p.key}-${f.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-ra-amber/15 text-ra-amberDeep">
                          <FI size={18} />
                        </div>
                        <h3 className="mt-4 font-display text-base font-semibold text-ra-ink">{f.name}</h3>
                        <p className="mt-1.5 text-sm text-ra-ink/70 leading-relaxed">{f.body}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>
        );
      })}

      <CTABand
        eyebrow="See it on your fleet"
        title="One demo. Your actual rules."
        sub="Bring your weirdest workflow. We'll configure it live."
      />
    </>
  );
}
